import { useGetParentViewSessionDetails } from '@actions';
import LanguageSwitcher from '@components/V3/LanguageSwitcher';
import Button from '@components/V4/Button';
import MonthlyNPSModal from '@modules/Base/PendingNPS/MonthlyNPSModal';
import SessionDetailsChat from '@modules/SessionDetails/SessionDetailsChat/SessionDetailsChat';
import SessionDetailsFeedback, {
  FeedbackSection,
} from '@modules/SessionDetails/SessionDetailsFeedback';
import SessionDetailsHeader from '@modules/SessionDetails/SessionDetailsHeader/SessionDetailsHeader';
import SessionDetailsNotes from '@modules/SessionDetails/SessionDetailsNotes/SessionDetailsNotes';
import { SessionDetailsContext } from '@modules/SessionDetails/context/SessionDetailsContext';
import { SessionStatus } from '@shared/enums';
import { useTranslation } from 'apps/agora/src/TranslationContext';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ParentPageSessionFeedbackModal from './components/ParentPageSessionFeedbackModal/ParentPageSessionFeedbackModal';
import useParentNpsModal from './hooks/useParentNpsModal';

const ParentPageSessionDetails = () => {
  const { sessionId, studentId } = useParams<{ sessionId: string; studentId: string }>();

  const [parentFeedbackModalData, setParentFeedbackModalData] = useState<string | undefined>(
    undefined
  );

  const firstUpgradeNPS = Math.random() < 0.5;
  const { t, language } = useTranslation();
  const history = useHistory();
  const toast = useToast();

  const fallbackColor = '#36B3A8';

  // get token from url params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const storedToken = localStorage.getItem('parentToken');
  const parentToken = token || storedToken || undefined;

  const { isNpsModalOpen } = useParentNpsModal({
    studentId,
    token: parentToken,
  });

  if (!storedToken && token) {
    if (token) {
      localStorage.setItem('parentToken', token);
    }
  }

  if (!token && !storedToken) {
    window.location.href = '/error';
  }

  const { data: sessionData, isLoading } = useGetParentViewSessionDetails(
    sessionId,
    { userId: studentId, token: parentToken ?? '' },
    {
      onError: (err: any) => {
        history.replace(`/parent-view/${studentId}`);
        toast.error(err?.response?.data?.error?.message || t('errors.unexpected'));
      },
    }
  );

  if (!isLoading && !sessionData) {
    return <></>;
  }

  const accentColor = sessionData?.course.color ?? fallbackColor;

  const mentorFeedbackSections: FeedbackSection[] = [
    {
      label: t('sessionDetails.mentorFeedback.sessionSummary'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.sessionSummaryRo ||
            sessionData?.mentorFeedback?.sessionSummary
          : sessionData?.mentorFeedback?.sessionSummary,
    },
    {
      label: t('sessionDetails.mentorFeedback.nextTopics'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.nextTopicsRo || sessionData?.mentorFeedback?.nextTopics
          : sessionData?.mentorFeedback?.nextTopics,
    },
    {
      label: t('sessionDetails.mentorFeedback.homework'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.homework?.explanationRo ||
            sessionData?.mentorFeedback?.homework?.explanation
          : sessionData?.mentorFeedback?.homework?.explanation,
      starRating: sessionData?.mentorFeedback?.homework?.rate,
    },
    {
      label: t('sessionDetails.mentorFeedback.engagement'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.engagement?.explanationRo ||
            sessionData?.mentorFeedback?.engagement?.explanation
          : sessionData?.mentorFeedback?.engagement?.explanation,
      starRating: sessionData?.mentorFeedback?.engagement?.rate,
    },
    {
      label: t('sessionDetails.mentorFeedback.progress'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.progress?.explanationRo ||
            sessionData?.mentorFeedback?.progress?.explanation
          : sessionData?.mentorFeedback?.progress?.explanation,
      starRating: sessionData?.mentorFeedback?.progress?.rate,
    },
    {
      label: t('sessionDetails.mentorFeedback.parentHelp'),
      value:
        language === 'ro'
          ? sessionData?.mentorFeedback?.parentHelpRo || sessionData?.mentorFeedback?.parentHelp
          : sessionData?.mentorFeedback?.parentHelp,
    },
  ];

  const studentFeedbackSections: FeedbackSection[] = [
    {
      label: t('sessionDetails.studentFeedback.sessionRating'),
      starRating: sessionData?.studentFeedback?.rating,
      ratingPosition: 'below',
    },
    {
      label: t('sessionDetails.studentFeedback.feedback'),
      value: sessionData?.studentFeedback?.message,
    },
  ];

  const openParentFeedbackModalHandler = (meetingId: string) => {
    setParentFeedbackModalData(meetingId);
  };

  const closeParentFeedbackModalHandler = () => {
    setParentFeedbackModalData(undefined);
  };

  return (
    <section className="flex flex-col gap-4 px-4 py-4 tablet:px-6 tablet:py-6 laptop:m-auto laptop:gap-8 largeLaptop:px-0 max-w-[1126px]">
      <div className="flex justify-between items-center">
        <Button
          onClick={() => history.push(`/parent-view/${studentId}`)}
          buttonText={t('common.back')}
        />

        <LanguageSwitcher />
      </div>

      <SessionDetailsContext.Provider value={{ isLoading, isParentView: true }}>
        <SessionDetailsHeader
          onMeetingModalOpen={() => {}}
          setIsMentorFeedbackOpen={() => {}}
          {...sessionData}
          abbreviation={sessionData?.course?.abbreviation}
          accentColor={accentColor}
        />

        <section className="flex flex-col gap-4 laptop:flex-row laptop:gap-8 largeLaptop:gap-14">
          <aside className="flex flex-col gap-4 tablet:order-2 laptop:gap-8 laptop:w-1/3 largeLaptop:min-w-[440px]">
            <SessionDetailsNotes
              notes={sessionData?.notes}
              meetingId={sessionData?.relevantMeetingId}
            />

            <SessionDetailsChat
              chatMessages={sessionData?.chatHistory}
              startTime={sessionData?.startTime}
              accentColor={accentColor}
            />
          </aside>

          <section className="flex flex-col gap-4 tablet:order-1 laptop:gap-8 laptop:w-2/3 largeLaptop:min-w-[630px]">
            <SessionDetailsFeedback
              isMentorFeedback
              className="tablet:order-2"
              feedbackSections={mentorFeedbackSections}
              needsFeedback={
                sessionData?.sessionStatus
                  ? sessionData.sessionStatus === SessionStatus.NEEDS_FEEDBACK
                  : false
              }
              accentColor={accentColor}
              hasFeedback={!!sessionData?.mentorFeedback?.engagement}
              setIsMentorFeedbackOpen={() => {}}
              setIsStudentFeedbackOpen={() => {}}
              sessionId={sessionId}
              meetingId={sessionData?.relevantMeetingId}
              openParentFeedbackModalHandler={openParentFeedbackModalHandler}
            />
            <SessionDetailsFeedback
              className="tablet:order-1"
              feedbackSections={studentFeedbackSections}
              accentColor={accentColor}
              needsFeedback={!!sessionData?.relevantMeetingId}
              areasOfImprovement={undefined}
              hasFeedback={
                !!sessionData?.studentFeedback?.message || !!sessionData?.studentFeedback?.rating
              }
              setIsMentorFeedbackOpen={() => {}}
              setIsStudentFeedbackOpen={() => {}}
              sessionId={sessionId}
              openParentFeedbackModalHandler={openParentFeedbackModalHandler}
              meetingId={sessionData?.relevantMeetingId}
            />
          </section>
        </section>
      </SessionDetailsContext.Provider>
      {isNpsModalOpen && (
        <MonthlyNPSModal
          visible={true}
          firstUpgradeNPS={firstUpgradeNPS}
          parentView={true}
          allowClose={false}
        />
      )}

      {parentFeedbackModalData && (
        <ParentPageSessionFeedbackModal
          sessionId={parentFeedbackModalData}
          onClose={closeParentFeedbackModalHandler}
          language={language}
        />
      )}
    </section>
  );
};

export default ParentPageSessionDetails;
